import React, { createContext, FC, useCallback, useContext, useEffect, useState } from 'react';
import { usePrevious } from 'react-use';
import { mediaManagerStorageStatusOpenMm } from '@wix/bi-logger-media-manager-g6/v2';
import { storagePlanUpgradedNotification } from '../../../../containers/FilesManager/notifications/storagePlanUpgradedNotification';
import { useAppEvents, EVENT_STORAGE_QUOTA_SERVER_ERROR } from '../../../../services/appEvents';
import { DuplexerEvent } from '../../../../constants/DuplexerEvent';
import { parentConnection as parentConnectionDefault } from '../../../../services/parentConnection';
import { bi } from '../../../../services/loggers/bi';
import { parseSiteMediaToken } from '../../../siteMediaToken';
import { useMMGRSettings } from '../../../hoc/withMMGRSettings';
import { useDuplexer, TDuplexerHandler } from '../../useDuplexer';
import { useNotificatorConsumer } from '../../useNotificator';
import { useFormatBytes } from '../../useFormattedBytes';
import { fetchQuotaData as fetchQuotaDataDefault } from './fetchQuotaData';
import { EventEmitterOnOff, IStorageQuotaData, IStorageQuotaDataBase } from './types';
import { useStorageQuotaExperiment } from './useStorageQuotaExperiment';
import { enrichStorageQuotaData } from './enrichStorageQuotaData';
import { calculateBiStatus } from '../useStorageQuotaBI';
import { BIPageName } from '../../../../constants/Bi';

export const StorageQuotaContext = createContext<{
  storageQuotaData?: IStorageQuotaData;
  isLoading: boolean;
  isStorageQuotaVisibilityEnabled: boolean;
  isStorageQuotaEnforcementEnabled: boolean;
}>({
  storageQuotaData: undefined,
  isLoading: false,
  isStorageQuotaVisibilityEnabled: false,
  isStorageQuotaEnforcementEnabled: false,
});

interface IStorageQuotaProvider {
  fetchQuotaData?: (siteMediaToken: string) => Promise<IStorageQuotaDataBase>;
  parentConnection?: EventEmitterOnOff;
}

const fireStorageStatusBI = (freshStorageQuotaData: IStorageQuotaData) => {
  const { usage, quota } = freshStorageQuotaData;
  const storageStatus = calculateBiStatus(freshStorageQuotaData);
  bi.logger().report(
    mediaManagerStorageStatusOpenMm({
      used_storage: usage.storage,
      storage_limit: quota.storage,
      storage_status: storageStatus,
      pageName: BIPageName.MediaManager,
    }),
  );
};

export const StorageQuotaProvider: FC<IStorageQuotaProvider> = ({
  children,
  fetchQuotaData = fetchQuotaDataDefault,
  parentConnection = parentConnectionDefault,
}) => {
  const { siteMediaToken, metaSiteToken } = useMMGRSettings().mmgrSettings;
  const { isStorageQuotaEnforcementEnabled, isStorageQuotaVisibilityEnabled } = useStorageQuotaExperiment();

  const [storageQuotaData, setStorageQuotaData] = useState<IStorageQuotaData>();
  const updateStorageQuotaData = useCallback(
    (freshStorageQuotaData: IStorageQuotaDataBase) => {
      const result = enrichStorageQuotaData(freshStorageQuotaData, isStorageQuotaEnforcementEnabled);
      setStorageQuotaData(result);
      return result;
    },
    [setStorageQuotaData, isStorageQuotaEnforcementEnabled],
  );
  const [isLoading, setLoading] = useState(false);

  const downloadFreshData = useCallback(async () => {
    setLoading(true);
    try {
      return updateStorageQuotaData(await fetchQuotaData(metaSiteToken));
    } finally {
      setLoading(false);
    }
  }, [fetchQuotaData, metaSiteToken, updateStorageQuotaData, setLoading]);

  useEffect(() => {
    if (metaSiteToken && !storageQuotaData && !isLoading) {
      downloadFreshData().then(fireStorageStatusBI);
    }
  }, [metaSiteToken, storageQuotaData, isLoading, downloadFreshData]);

  const { appEvents } = useAppEvents();
  useEffect(() => {
    const refreshOnReopen = () => downloadFreshData().then(fireStorageStatusBI);

    parentConnection.on('settings', refreshOnReopen);
    appEvents.on(EVENT_STORAGE_QUOTA_SERVER_ERROR, downloadFreshData);
    return () => {
      parentConnection.off('settings', refreshOnReopen);
      appEvents.off(EVENT_STORAGE_QUOTA_SERVER_ERROR, downloadFreshData);
    };
  }, [appEvents, downloadFreshData, parentConnection]);

  const previousQuota = usePrevious(storageQuotaData?.quota?.storage);
  const { notify } = useNotificatorConsumer();
  const formatBytes = useFormatBytes();
  useEffect(() => {
    if (!isStorageQuotaVisibilityEnabled) {
      return;
    }

    const currentUsage = storageQuotaData?.usage?.storage;
    const currentQuota = storageQuotaData?.quota?.storage;
    const spaceAvailable = currentQuota - currentUsage;

    if (!currentQuota || !previousQuota || !currentUsage || spaceAvailable < 0) {
      return;
    }

    if (currentQuota > previousQuota) {
      const isUpgradeAvailable = !!storageQuotaData?.quota?.plans?.upgradeUrl;
      notify(storagePlanUpgradedNotification(formatBytes(spaceAvailable), isUpgradeAvailable), {
        timeout: 30000,
      });
    }
  }, [isStorageQuotaVisibilityEnabled, storageQuotaData, previousQuota, notify, formatBytes]);

  useStorageUsageUpdateMessages((message) => {
    const { storage: usage, lastProcessed } = message.info.data.total.usage;
    const lastProcessedDateInUpdate = new Date(lastProcessed);
    const lastProcessedDateInState = storageQuotaData?.usage?.lastProcessed;
    const isOldUpdate = !!lastProcessedDateInState && lastProcessedDateInState > lastProcessedDateInUpdate;

    if (!storageQuotaData || isOldUpdate) {
      return;
    }

    updateStorageQuotaData({
      ...storageQuotaData,
      usage: {
        ...storageQuotaData.usage,
        storage: usage,
        lastProcessed: lastProcessedDateInUpdate,
      },
    });
  }, isStorageQuotaVisibilityEnabled);

  const { metaSiteId } = parseSiteMediaToken(siteMediaToken);
  useDuplexer({
    [DuplexerEvent.PremiumPlanChanged]: (message) => {
      if (!isStorageQuotaVisibilityEnabled) {
        return;
      }
      if (message.info.data.siteId === metaSiteId) {
        downloadFreshData();
      }
    },
  });

  return (
    <StorageQuotaContext.Provider
      value={{
        storageQuotaData,
        isLoading,
        isStorageQuotaVisibilityEnabled,
        isStorageQuotaEnforcementEnabled,
      }}
    >
      {children}
    </StorageQuotaContext.Provider>
  );
};

export const useStorageQuota = () => useContext(StorageQuotaContext);

export const useStorageUsageUpdateMessages = (handler: TDuplexerHandler, isStorageQuotaVisibilityEnabled: boolean) => {
  const { metaSiteId } = parseSiteMediaToken(useMMGRSettings().mmgrSettings.siteMediaToken);

  useDuplexer({
    [DuplexerEvent.StorageUsageUpdate]: (message) => {
      if (!isStorageQuotaVisibilityEnabled) {
        return;
      }

      const metaSiteIdFromMessage = message.info.data.total.id;
      if (metaSiteId === metaSiteIdFromMessage) {
        handler(message);
      }
    },
  });
};
